import type { MouseEvent } from "react";
import { Link } from "react-router";
import { useTranslate } from "ra-core";
import {
  Bot,
  CheckCircle2,
  Film,
  Hand,
  Loader2,
  MessageCircle,
  RotateCcw,
} from "lucide-react";

import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { contactWhatsAppSectionPath } from "../../contacts/contactDeepLinks";
import type { InboxConversation, InboxState } from "../../types";
import { PriorityDot, ReasonChip, WindowBadge } from "./InboxBadges";
import { FelixHandoffStatus } from "./FelixHandoffStatus";
import {
  attentionReasonLabel,
  flagEmoji,
  formatRelativeTime,
  inboxContactName,
  initialsOf,
  webCreateStall,
} from "./inboxModel";
import type { InboxTransitionAction } from "./useInbox";

export interface InboxTableProps {
  rows: InboxConversation[];
  state: InboxState;
  now: number;
  onOpen: (row: InboxConversation) => void;
  onTransition: (row: InboxConversation, action: InboxTransitionAction) => void;
  isPending: (contactId: InboxConversation["contact_id"]) => boolean;
}

export const InboxTable = ({
  rows,
  state,
  now,
  onOpen,
  onTransition,
  isPending,
}: InboxTableProps) => {
  const translate = useTranslate();
  const t = (key: string, options?: Record<string, unknown>) =>
    translate(`crm.whatsapp.inbox.${key}`, options);

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="min-w-[220px]">
              {t("columns.contact")}
            </TableHead>
            <TableHead className="min-w-[280px]">
              {t("columns.reason")}
            </TableHead>
            <TableHead className="w-[130px]">{t("columns.waiting")}</TableHead>
            <TableHead className="hidden w-[170px] md:table-cell">
              {t("columns.window")}
            </TableHead>
            <TableHead className="hidden w-[160px] lg:table-cell">
              {t("columns.owner")}
            </TableHead>
            <TableHead className="w-[260px] text-right">
              {t("columns.actions")}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <InboxTableRow
              key={String(row.contact_id)}
              row={row}
              state={state}
              now={now}
              onOpen={onOpen}
              onTransition={onTransition}
              pending={isPending(row.contact_id)}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

const InboxTableRow = ({
  row,
  state,
  now,
  onOpen,
  onTransition,
  pending,
}: {
  row: InboxConversation;
  state: InboxState;
  now: number;
  onOpen: (row: InboxConversation) => void;
  onTransition: (row: InboxConversation, action: InboxTransitionAction) => void;
  pending: boolean;
}) => {
  const translate = useTranslate();
  const t = (key: string, options?: Record<string, unknown>) =>
    translate(`crm.whatsapp.inbox.${key}`, options);
  const name = inboxContactName(row);
  const flag = flagEmoji(row.phone_country_iso2);
  const phone = row.phone_e164 || row.whatsapp_user_id;
  const waiting = formatRelativeTime(row.attention_since, now);
  const count = row.attention_count ?? 0;
  const owner =
    state === "resolved"
      ? row.attention_resolved_by_name
      : row.attention_claimed_by_name;
  const rowState = row.attention_state;
  // MYC-090: the customer waits on my.echoflicks.com — point at the Echo.
  const webStall = webCreateStall(row);
  const act =
    (action: InboxTransitionAction) => (event: MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      onTransition(row, action);
    };

  return (
    <TableRow
      className="cursor-pointer"
      onClick={() => onOpen(row)}
      data-contact-id={String(row.contact_id)}
    >
      <TableCell>
        <div className="flex items-center gap-3">
          <Avatar className="size-9 shrink-0">
            <AvatarFallback className="text-xs">
              {initialsOf(name)}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 font-medium">
              <span className="truncate">{name}</span>
              {flag ? (
                <span aria-label={row.phone_country_iso2 ?? undefined}>
                  {flag}
                </span>
              ) : null}
            </div>
            <div className="truncate font-mono text-xs text-muted-foreground">
              {phone}
            </div>
          </div>
        </div>
      </TableCell>
      <TableCell>
        <div className="flex items-start gap-2">
          <PriorityDot priority={row.attention_priority} className="mt-1.5" />
          <div className="min-w-0 space-y-1">
            <ReasonChip label={attentionReasonLabel(row)} />
            {webStall ? (
              <div className="text-xs font-medium text-muted-foreground">
                {t(`web_stall.${webStall.stage ?? "unknown"}`)}
              </div>
            ) : null}
            <FelixHandoffStatus row={row} now={now} />
            {row.attention_preview ? (
              <p className="line-clamp-2 text-sm text-muted-foreground">
                {row.attention_preview}
              </p>
            ) : null}
          </div>
        </div>
      </TableCell>
      <TableCell>
        <div className="text-sm tabular-nums">{waiting || "—"}</div>
        {count > 0 ? (
          <div className="text-xs text-muted-foreground">
            {t("messages_count", { smart_count: count })}
          </div>
        ) : null}
      </TableCell>
      <TableCell className="hidden md:table-cell">
        <WindowBadge expiresAt={row.freeform_window_expires_at} now={now} />
      </TableCell>
      <TableCell className="hidden text-sm lg:table-cell">
        {owner ? (
          <span>{owner}</span>
        ) : (
          <span className="text-muted-foreground">{t("unassigned")}</span>
        )}
      </TableCell>
      <TableCell className="text-right">
        <div
          className="flex flex-wrap justify-end gap-1.5"
          onClick={(event) => event.stopPropagation()}
        >
          {rowState === "needs_reply" ? (
            <Button
              size="sm"
              onClick={act("claim")}
              disabled={pending}
              title={t("actions.claim_hint")}
            >
              {pending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Hand className="size-4" />
              )}
              {t("actions.claim")}
            </Button>
          ) : null}
          {webStall?.echoPath ? (
            <Button size="sm" variant="outline" asChild>
              <Link to={webStall.echoPath}>
                <Film className="size-4" />
                {t("actions.open_echo")}
              </Link>
            </Button>
          ) : null}
          <Button size="sm" variant="outline" asChild>
            <Link to={contactWhatsAppSectionPath(row.contact_id)}>
              <MessageCircle className="size-4" />
              {t("actions.open_chat")}
            </Link>
          </Button>
          {rowState === "claimed" ? (
            <Button
              size="sm"
              variant="outline"
              onClick={act("release")}
              disabled={pending}
              title={t("actions.release_hint")}
            >
              <Bot className="size-4" />
              {t("actions.release")}
            </Button>
          ) : null}
          {rowState === "needs_reply" || rowState === "claimed" ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={act("resolve")}
              disabled={pending}
            >
              <CheckCircle2 className="size-4" />
              {t("actions.resolve")}
            </Button>
          ) : null}
          {rowState === "resolved" ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={act("reopen")}
              disabled={pending}
            >
              <RotateCcw className="size-4" />
              {t("actions.reopen")}
            </Button>
          ) : null}
        </div>
      </TableCell>
    </TableRow>
  );
};
